import React, { useState } from "react";
import { Box } from "@mui/material";
import { SPORT_ICONS } from "./sportIcons";
import { getTeamLogoUrl, resolveMlbTeamId } from "../utils/teamLogoUrl";
import { getNbaTeamLogoUrl } from "../constants/nbaFranchiseIds";
import { getNflTeamLogoUrl } from "../constants/nflFranchiseIds";

const FallbackIcon = ({ sport, size }) => {
  const Icon = SPORT_ICONS[sport] || SPORT_ICONS.mlb;
  return <Icon sx={{ fontSize: size * 0.55 }} />;
};

function resolveTeamLogoUrl({
  sport,
  franchiseId,
  teamName,
  mlbTeamId,
  variant,
  imageError,
}) {
  if (imageError) return null;
  if (sport === "nba") return getNbaTeamLogoUrl(franchiseId || teamName);
  if (sport === "nfl") return getNflTeamLogoUrl(franchiseId || teamName);
  const mlbId = mlbTeamId ?? resolveMlbTeamId(teamName);
  return mlbId ? getTeamLogoUrl(mlbId, { variant }) : null;
}

/**
 * Team mark from the sport CDN, with a generic icon when unmapped.
 */
const TeamLogo = ({
  mlbTeamId,
  teamName,
  sport = "mlb",
  franchiseId,
  size = 32,
  variant = "dark",
  alt,
  sx = {},
  ...props
}) => {
  const [imageError, setImageError] = useState(false);
  const usesFranchiseLogo = sport === "nba" || sport === "nfl";
  const resolvedMlbId = usesFranchiseLogo
    ? null
    : mlbTeamId ?? resolveMlbTeamId(teamName);
  const logoUrl = resolveTeamLogoUrl({
    sport,
    franchiseId,
    teamName,
    mlbTeamId,
    variant,
    imageError,
  });
  const label =
    alt || teamName || (resolvedMlbId ? `Team ${resolvedMlbId}` : "Team");

  if (!logoUrl) {
    return (
      <Box
        aria-label={label}
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
          bgcolor: "action.hover",
          color: "text.secondary",
          flexShrink: 0,
          ...sx,
        }}
        {...props}
      >
        <FallbackIcon sport={sport} size={size} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={logoUrl}
      alt={label}
      onError={() => setImageError(true)}
      sx={{
        width: size,
        height: size,
        objectFit: "contain",
        flexShrink: 0,
        display: "block",
        ...sx,
      }}
      {...props}
    />
  );
};

export default TeamLogo;
